// ============================================================================
// components/block/liveStreamManagment/DeckMixerPanel.tsx
//
// Bloc « Mixeur » du deck (2026-10-20) : un raccourci vers les niveaux audio des
// sources du deck. Une voie par emplacement occupé, qui DOUBLE la section
// « Niveaux audio » et le bouton « Son actif / coupé » de la configuration de la
// source : mêmes états (stereoVolumes, SourceAudioConfig.muted), mêmes setters,
// en direct. Bouger un fader ici déplace celui de la config, et inversement.
//
// Sans rapport avec la fenêtre Mixeur de la barre (Switch audio, mode Mix).
//
// Un seul fader par source : il déplace G et D du même nombre de dB, comme les
// voies liées de la config — l'écart réglé là-bas est conservé. Il affiche la
// plus forte des deux voies.
//
// Le haut-parleur à côté du titre allume une sortie de son propre au bloc : les
// sources du deck à leurs réglages, sur les enceintes de la régie seulement
// (cf. useDeckMixMonitor). Ni l'antenne ni le son du flux final n'en dépendent.
// ============================================================================

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SlidersHorizontal, Volume2, VolumeX, Settings } from 'lucide-react';
import DeckMixerEffectsModal from './DeckMixerEffectsModal';
import type { AudioSourceRegistry } from '../../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry';
import { SourceAudioConfig } from '../../../contexts/MediaStreamContext';
import { StereoVolume, DEFAULT_STEREO_VOLUME, AudioEffectState, DEFAULT_AUDIO_EFFECTS } from './SourceConfigModal';
import { useDeckMixMonitor } from '../../../hooks/liveStreamManagment/useDeckMixMonitor';
import HlsAudioPanel, { gainToSlider, sliderToGain } from './HlsAudioPanel';

export interface DeckMixerStrip { sourceId: string; label: string; }

interface DeckMixerPanelProps {
  strips               : DeckMixerStrip[];
  registry            ?: AudioSourceRegistry;
  getSourceAudioConfig : (sourceId: string) => SourceAudioConfig;
  stereoVolumes       ?: Map<string, StereoVolume>;
  onStereoVolumeChange?: (sourceId: string, stereo: StereoVolume) => void;
  onMuteToggle         : (sourceId: string) => void;
  /** Effets audio de chaque source : l'écoute du bloc les reproduit. */
  audioEffectsMap       ?: Map<string, AudioEffectState>;
  audioEffectConfigsMap ?: Map<string, Record<string, unknown>>;
  /** Engrenage d'une voie : fenêtre des effets, en direct (cf. DeckMixerEffectsModal). */
  onToggleAudioEffect       ?: (sourceId: string, effectId: string) => void;
  onAudioEffectConfigsChange?: (sourceId: string, configs: Record<string, unknown>) => void;
}

const STRIP_W       = 96;
// Ligne du nom de la voie + son MUTE
const STRIP_HEAD_H  = 24;
// Réserve pour la barre de défilement horizontale
const SCROLLBAR_H   = 10;

const DeckMixerPanel: React.FC<DeckMixerPanelProps> = ({
  strips, registry, getSourceAudioConfig, stereoVolumes, onStereoVolumeChange, onMuteToggle,
  audioEffectsMap, audioEffectConfigsMap, onToggleAudioEffect, onAudioEffectConfigsChange,
}) => {
  // Voie dont la fenêtre des effets est ouverte
  const [effectsFor, setEffectsFor] = useState<DeckMixerStrip | null>(null);

  // Écoute du bloc : sortie de son à part (cf. useDeckMixMonitor), coupée au départ.
  const [listening, setListening] = useState(false);
  useDeckMixMonitor(registry, listening, strips.map(({ sourceId }) => {
    const cfg = getSourceAudioConfig(sourceId);
    return {
      sourceId,
      stereo       : stereoVolumes?.get(sourceId) ?? DEFAULT_STEREO_VOLUME,
      volume       : cfg.volume ?? 1,
      muted        : cfg.muted,
      effects      : (audioEffectsMap?.get(sourceId) ?? DEFAULT_AUDIO_EFFECTS) as unknown as Record<string, boolean>,
      effectConfigs: audioEffectConfigsMap?.get(sourceId) ?? {},
    };
  }));

  // Valeurs émises par ce bloc et pas encore revenues du parent : deux événements
  // du fader peuvent arriver avant le rendu suivant.
  const pendingRef = useRef(new Map<string, StereoVolume>());
  useEffect(() => { pendingRef.current.clear(); }, [stereoVolumes]);

  const stereoOf = (id: string): StereoVolume =>
    pendingRef.current.get(id) ?? stereoVolumes?.get(id) ?? DEFAULT_STEREO_VOLUME;

  const handleVolume = (id: string, sliderVal: number) => {
    if (!onStereoVolumeChange) return;
    const cur  = stereoOf(id);
    const ref  = Math.max(cur.left, cur.right);
    const g    = sliderToGain(sliderVal);
    const next = ref <= 0 ? { left: g, right: g } : { left: cur.left * g / ref, right: cur.right * g / ref };
    pendingRef.current.set(id, next);
    onStereoVolumeChange(id, next);
  };

  // Les voies prennent toute la hauteur disponible dans le deck.
  const areaRef = useRef<HTMLDivElement>(null);
  const [areaH, setAreaH] = useState(0);
  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => setAreaH(entries[0].contentRect.height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const panelH = Math.max(100, areaH - STRIP_HEAD_H - SCROLLBAR_H);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
        <span style={{ display: 'flex', alignItems: 'center', opacity: 0.7 }}>
          <SlidersHorizontal className="w-3 h-3" />
        </span>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#ffffff' }}>Mixeur</span>
        <button
          onClick={() => { registry?.resume().catch(() => {}); setListening(v => !v); }}
          aria-pressed={listening}
          title={listening
            ? "Couper l'écoute du Mixeur (l'antenne et le son du flux final ne sont pas touchés)"
            : "Écouter les sources du deck à leurs réglages (l'antenne et le son du flux final ne sont pas touchés)"}
          style={{
            marginLeft: 4, padding: 0, width: 20, height: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none', cursor: 'pointer',
            color: listening ? '#25C400' : '#ef4444',
          }}
        >
          {listening ? <Volume2 style={{ width: 14, height: 14 }} /> : <VolumeX style={{ width: 14, height: 14 }} />}
        </button>
      </div>

      <div
        ref={areaRef}
        style={{
          flex: 1, minHeight: 0,
          borderRadius: 8,
          border: '1px solid rgba(255,255,255,0.10)',
          background: 'rgba(15,23,42,0.45)',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.4)',
          overflowX: 'auto', overflowY: 'hidden',
          color: '#ffffff', fontFamily: 'monospace', fontSize: 11,
        }}
      >
        {strips.length === 0 ? (
          <div style={{ padding: 10, color: 'rgba(148,163,184,0.85)', fontStyle: 'italic' }}>
            Aucune source dans le deck
          </div>
        ) : areaH > 0 && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', padding: '0 4px', width: 'max-content' }}>
            {strips.map(({ sourceId, label }) => {
              const muted  = getSourceAudioConfig(sourceId).muted;
              const stereo = stereoVolumes?.get(sourceId) ?? DEFAULT_STEREO_VOLUME;
              return (
                <div key={sourceId} style={{ display: 'flex', flexDirection: 'column', width: STRIP_W, flexShrink: 0 }}>
                  <div style={{ height: STRIP_HEAD_H, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span title={label} style={{
                      flex: 1, minWidth: 0, fontSize: 11, fontWeight: 700,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {label}
                    </span>
                    <button
                      onClick={() => onMuteToggle(sourceId)}
                      aria-pressed={muted}
                      title={muted ? 'Son coupé (le niveau est conservé) — cliquer pour rétablir' : 'Couper le son de cette source sans toucher au niveau'}
                      style={{
                        flexShrink: 0, height: 18, padding: '0 4px', borderRadius: 4, cursor: 'pointer',
                        fontSize: 9, fontWeight: 800, fontFamily: 'monospace', letterSpacing: '0.5px',
                        color:      muted ? '#052e0b' : 'rgba(255,255,255,0.8)',
                        background: muted ? '#25C400' : 'rgba(255,255,255,0.07)',
                        border:     `1px solid ${muted ? '#25C400' : 'rgba(255,255,255,0.18)'}`,
                        boxShadow:  muted ? '0 0 8px rgba(37,196,0,0.7)' : 'none',
                        transition: 'background 0.15s, box-shadow 0.15s',
                      }}
                    >
                      MUTE
                    </button>
                  </div>
                  <div style={{ height: panelH, display: 'flex' }}>
                    <HlsAudioPanel
                      meterSourceId={sourceId}
                      meterRegistry={registry}
                      externalVolume={gainToSlider(Math.max(stereo.left, stereo.right))}
                      externalMuted={muted}
                      onVolumeChange={v => handleVolume(sourceId, v)}
                      hideMeterMute
                      footerExtra={onToggleAudioEffect && (
                        <button
                          onClick={() => setEffectsFor({ sourceId, label })}
                          title="Effets audio de cette source"
                          style={{
                            flexShrink: 0, width: 20, height: 20, padding: 0, borderRadius: 4,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                            background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.13)',
                            color: 'rgba(255,255,255,0.75)',
                          }}
                        >
                          <Settings style={{ width: 12, height: 12 }} />
                        </button>
                      )}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {effectsFor && onToggleAudioEffect && (
        <DeckMixerEffectsModal
          sourceId={effectsFor.sourceId}
          label={effectsFor.label}
          effects={audioEffectsMap?.get(effectsFor.sourceId)}
          effectConfigs={audioEffectConfigsMap?.get(effectsFor.sourceId)}
          onToggle={onToggleAudioEffect}
          onConfigsChange={onAudioEffectConfigsChange}
          onClose={() => setEffectsFor(null)}
        />
      )}
    </div>
  );
};

export default DeckMixerPanel;
